<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatAmount, formatMoney, formatRate, toIsoDate } from '../utils/format'
import type {
  CashFlowSummaryTotals,
  FinancialAccount,
  Payment,
  PayablesAgingSummary,
  RevenueVsCollectionsTotals,
} from '../types/finance'

const { t } = useI18n()
const auth = useAuthStore()
const {
  listFinancialAccounts,
  listPayments,
  payablesAging,
  cashFlowSummary,
  revenueVsCollections,
  currencyExposure,
} = useFinanceApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canAccounts = computed(() => permissions.value.includes('financial_accounts.view'))
const canPayments = computed(() => permissions.value.includes('payments.view'))
const canReports = computed(() => permissions.value.includes('finance_reports.view'))

const loading = ref(true)
const error = ref('')
const warnings = ref<string[]>([])

const accounts = ref<FinancialAccount[]>([])
const payments = ref<Payment[]>([])
const payables = ref<PayablesAgingSummary | null>(null)
const cashFlow = ref<CashFlowSummaryTotals | null>(null)
const revenue = ref<RevenueVsCollectionsTotals | null>(null)

const today = toIsoDate()
const monthStart = `${today.slice(0, 7)}-01`

/** Cash position per currency, derived from the account balances. */
const cashByCurrency = computed(() => {
  const totals = new Map<string, number>()
  for (const account of accounts.value) {
    const code = account.currency_code || '—'
    totals.set(code, (totals.get(code) ?? 0) + Number(account.current_balance ?? 0))
  }
  return [...totals.entries()]
    .map(([code, amount]) => ({ code, amount }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
})

const activeAccounts = computed(() => accounts.value.filter((account) => account.is_active === 1).length)

const recentPayments = computed(() =>
  [...payments.value]
    .sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')))
    .slice(0, 6),
)

async function loadAccounts() {
  if (!canAccounts.value) return
  try {
    accounts.value = await listFinancialAccounts({ is_active: 1 })
  } catch (cause) {
    warnings.value.push(formatApiError(cause))
  }
}

async function loadPayments() {
  if (!canPayments.value) return
  try {
    payments.value = await listPayments()
  } catch (cause) {
    warnings.value.push(formatApiError(cause))
  }
}

async function loadReports() {
  if (!canReports.value) return
  const [payablesResult, cashFlowResult, revenueResult, exposureResult] = await Promise.allSettled([
    payablesAging({ as_of_date: today, per_page: 200 }),
    cashFlowSummary({ date_from: monthStart, date_to: today, group_by: 'month' }),
    revenueVsCollections({ date_from: monthStart, date_to: today, group_by: 'month' }),
    currencyExposure(today),
  ])

  if (payablesResult.status === 'fulfilled') payables.value = payablesResult.value.summary
  else warnings.value.push(formatApiError(payablesResult.reason))

  if (cashFlowResult.status === 'fulfilled') cashFlow.value = cashFlowResult.value.summary
  else warnings.value.push(formatApiError(cashFlowResult.reason))

  if (revenueResult.status === 'fulfilled') revenue.value = revenueResult.value.summary
  else warnings.value.push(formatApiError(revenueResult.reason))

  if (exposureResult.status === 'fulfilled') {
    warnings.value.push(...(exposureResult.value.summary?.warnings ?? []))
  } else {
    warnings.value.push(formatApiError(exposureResult.reason))
  }
}

async function load() {
  loading.value = true
  error.value = ''
  warnings.value = []
  try {
    await Promise.all([loadAccounts(), loadPayments(), loadReports()])
    if (!canAccounts.value && !canPayments.value && !canReports.value) {
      error.value = t('finance.common.noPermission')
    }
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function refresh() {
  await load()
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.overview.title') }}</h2>
  </div>

  <div class="toolbar">
    <div class="summary">
      <span>{{ t('finance.overview.asOf', { date: formatDisplayDate(today) }) }}</span>
    </div>
    <span class="spacer" />
    <button class="btn-secondary" :disabled="loading" @click="refresh">
      {{ t('finance.common.refresh') }}
    </button>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else>
    <section v-if="warnings.length" class="notice">
      <strong>{{ t('finance.overview.warnings') }}</strong>
      <p v-for="warning in warnings" :key="warning">{{ warning }}</p>
    </section>

    <div class="quick-grid">
      <RouterLink class="quick-card" to="/treasury/accounts">
        <strong>{{ t('finance.overview.quickAccounts') }}</strong>
        <span>{{ t('finance.overview.quickAccountsHint') }}</span>
      </RouterLink>
      <RouterLink class="quick-card" to="/treasury/transfers">
        <strong>{{ t('finance.nav.transfers') }}</strong>
        <span>{{ t('finance.overview.quickAccountsHint') }}</span>
      </RouterLink>
    </div>

    <div class="summary-row">
      <div class="summary-card accent">
        <span class="summary-label">{{ t('finance.overview.cashPosition') }}</span>
        <template v-if="cashByCurrency.length">
          <span v-for="line in cashByCurrency" :key="line.code" class="summary-value">
            {{ formatMoney(line.amount, line.code) }}
          </span>
        </template>
        <span v-else class="summary-value">—</span>
        <span class="summary-label">
          {{ t('finance.overview.cashAccounts') }}: {{ activeAccounts }} / {{ accounts.length }}
        </span>
      </div>

      <div class="summary-card">
        <span class="summary-label">{{ t('finance.overview.invoiced') }}</span>
        <span class="summary-value">
          {{ revenue ? formatAmount(revenue.invoiced_total) : '—' }}
          <small v-if="revenue">{{ formatRate(revenue.collection_rate) }}</small>
        </span>
        <span class="summary-label">{{ t('finance.overview.collected') }}:
          {{ revenue ? formatAmount(revenue.collected_total) : '—' }}
        </span>
      </div>

      <div class="summary-card" :class="{ attention: (payables?.bucket_60_plus ?? 0) > 0 }">
        <span class="summary-label">{{ t('finance.overview.payables') }}</span>
        <span class="summary-value">
          {{ payables ? formatAmount(payables.total_payables) : '—' }}
        </span>
        <span class="summary-label">
          {{ t('finance.overview.overdue') }}:
          {{ payables ? formatAmount(payables.bucket_60_plus) : '—' }}
        </span>
      </div>

      <div class="summary-card">
        <span class="summary-label">{{ t('finance.overview.netCashFlow') }}</span>
        <span class="summary-value">
          {{ cashFlow ? formatAmount(cashFlow.net_cash_flow) : '—' }}
        </span>
        <span class="summary-label">
          {{ t('finance.overview.cashIn') }}: {{ cashFlow ? formatAmount(cashFlow.cash_in_total) : '—' }}
          · {{ t('finance.overview.cashOut') }}:
          {{ cashFlow ? formatAmount(cashFlow.cash_out_total) : '—' }}
        </span>
      </div>
    </div>

    <div class="detail-grid">
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('finance.overview.quickReceivables') }}</h3>
        </div>
        <table v-if="revenue" class="data-table compact">
          <tbody>
            <tr>
              <td class="field-label">{{ t('finance.overview.collected') }}</td>
              <td class="field-value cell-num">{{ formatAmount(revenue.collected_total) }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.overview.collectionGap') }}</td>
              <td class="field-value cell-num">{{ formatAmount(revenue.collection_gap) }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.overview.collectionRate') }}</td>
              <td class="field-value cell-num">{{ formatRate(revenue.collection_rate) }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.adjustmentType.write_off') }}</td>
              <td class="field-value cell-num">{{ formatAmount(revenue.write_offs) }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.adjustmentType.customer_credit') }}</td>
              <td class="field-value cell-num">{{ formatAmount(revenue.credit_notes) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="empty-inline">{{ t('finance.common.empty') }}</p>
      </section>

      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('finance.overview.recentPayments') }}</h3>
          <RouterLink class="link-action" to="/treasury/accounts">
            {{ t('finance.nav.accounts') }}
          </RouterLink>
        </div>
        <div v-if="recentPayments.length" class="table-scroll">
          <table class="data-table compact">
            <thead>
              <tr>
                <th>{{ t('finance.common.date') }}</th>
                <th>{{ t('finance.common.agent') }}</th>
                <th>{{ t('finance.common.account') }}</th>
                <th class="cell-num">{{ t('finance.common.amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in recentPayments" :key="payment.id">
                <td class="cell-date">{{ formatDisplayDate(payment.created_at) }}</td>
                <td>{{ payment.agent_name || '—' }}</td>
                <td>{{ payment.financial_account_name || '—' }}</td>
                <td class="cell-num">{{ formatMoney(payment.amount, payment.currency_code) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty-inline">
          {{ canPayments ? t('finance.overview.noPayments') : t('finance.common.noPermission') }}
        </p>
      </section>
    </div>
  </template>
</template>
